import { useTranslation } from "react-i18next";

const SectionLabel = ({ children, inverted }) => (
  // The class bumps the size on mobile - see .prompter-section-label in Custom.css.
  <p className="prompter-section-label" style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: inverted ? "#038edc" : "#5fd0f3", marginBottom: 16 }}>
    {children}
  </p>
);

const SettingRow = ({ label, value, unit, children, inverted }) => (
  <div style={{ marginBottom: 20 }}>
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
      <label style={{ fontSize: 13, fontWeight: 500, color: inverted ? "#374151" : "rgba(255,255,255,0.75)", fontFamily: "Montserrat, sans-serif" }}>
        {label}
      </label>
      <span style={{ fontSize: 12, fontWeight: 700, color: "#038edc", background: "rgba(3,142,220,0.12)", padding: "2px 10px", borderRadius: 20 }}>
        {value}{unit}
      </span>
    </div>
    {children}
  </div>
);

const sliderStyle = {
  width: "100%",
  accentColor: "#038edc",
  cursor: "pointer",
};

const ToggleRow = ({ label, checked, onChange, id, inverted }) => (
  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 0", borderBottom: `1px solid ${inverted ? "rgba(0,0,0,0.08)" : "rgba(255,255,255,0.06)"}` }}>
    <label htmlFor={id} style={{ fontSize: 13, fontWeight: 500, color: inverted ? "#374151" : "rgba(255,255,255,0.75)", fontFamily: "Montserrat, sans-serif", cursor: "pointer" }}>
      {label}
    </label>
    <div
      onClick={() => onChange({ target: { checked: !checked } })}
      style={{
        width: 42, height: 24, borderRadius: 12, position: "relative", cursor: "pointer",
        background: checked ? "#038edc" : "rgba(255,255,255,0.12)",
        transition: "background 0.2s ease", flexShrink: 0,
      }}
    >
      <div style={{
        position: "absolute", top: 3, left: checked ? 21 : 3,
        width: 18, height: 18, borderRadius: "50%", background: "#fff",
        transition: "left 0.2s ease", boxShadow: "0 1px 4px rgba(0,0,0,0.3)",
      }} />
    </div>
  </div>
);

const TeleprompterSettings = ({
  setSpeed, speed,
  setMarginBottom, setMarginTop,
  setPaddingLeft, setPaddingRight,
  setFontSize, setCountdown, countdown,
  startMode, setStartMode,
  setMirrorHorizontal, setMirrorVertical, setInvertColors,
  mirrorHorizontal, mirrorVertical, invertColors, inverted, fontSize, paddingLeft, paddingRight,
}) => {
  const { t } = useTranslation();

  const MIN = 1;
  const MAX = 200;
  const uiValue = MIN + MAX - speed;
  const muted = inverted ? "rgba(0,0,0,0.35)" : "rgba(255,255,255,0.35)";
  const labelColor = inverted ? "#374151" : "rgba(255,255,255,0.75)";
  const chipBg = inverted ? "rgba(0,0,0,0.06)" : "rgba(255,255,255,0.08)";
  const chipColor = inverted ? "#374151" : "rgba(255,255,255,0.85)";

  const startModeOptions = [
    { value: "countdown", label: "Countdown only" },
    { value: "countdownAndStartbutton", label: "Countdown & Startbutton" },
    { value: "startbutton", label: "Startbutton only" },
  ];

  return (
    <div>
      {/* ── Start Mode (own section, full width) ── */}
      <div style={{ marginBottom: 28, paddingBottom: 24, borderBottom: `1px solid ${inverted ? "rgba(0,0,0,0.08)" : "rgba(255,255,255,0.07)"}` }}>
        <SectionLabel inverted={inverted}>Start Mode</SectionLabel>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {startModeOptions.map((opt) => (
            <button key={opt.value} onClick={() => setStartMode(opt.value)} style={{
              flex: "1 1 180px", padding: "10px 12px", borderRadius: 8, border: "none", cursor: "pointer",
              fontWeight: 700, fontSize: 13, fontFamily: "Montserrat, sans-serif",
              background: startMode === opt.value ? "#038edc" : chipBg,
              color: startMode === opt.value ? "#fff" : chipColor,
              transition: "background 0.15s ease",
            }}>
              {opt.label}
            </button>
          ))}
        </div>
      </div>

    <div className="prompter-settings-grid">

      {/* ── Text column ── */}
      <div>
        <SectionLabel inverted={inverted}>{t("Backend.Post.Teleprompter.Headers.Text")}</SectionLabel>

        <SettingRow label={t("Backend.Post.Teleprompter.ChangeSpeed")} value={uiValue} unit="" inverted={inverted}>
          <input type="range" style={sliderStyle} min={MIN} max={MAX} step={1} value={uiValue}
            onChange={(e) => setSpeed(MIN + MAX - Number(e.target.value))} />
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: muted, marginTop: 4 }}>
            <span>{t("Backend.Post.Teleprompter.Speeds.Slow")}</span>
            <span>{t("Backend.Post.Teleprompter.Speeds.Fast")}</span>
          </div>
        </SettingRow>

        <SettingRow label={t("Backend.Post.Teleprompter.FontSize")} value={fontSize} unit="px" inverted={inverted}>
          <input type="range" style={sliderStyle} min={10} max={100} step={1} value={fontSize}
            onChange={(e) => setFontSize(Number(e.target.value))} />
        </SettingRow>

        <div style={{ marginBottom: 20 }}>
          <label style={{ fontSize: 13, fontWeight: 500, color: labelColor, marginBottom: 8, display: "block" }}>
            {t("Backend.Post.Teleprompter.Countdown")}
          </label>
          <div style={{ display: "flex", gap: 8 }}>
            {[5, 10, 15].map((v) => (
              <button key={v} onClick={() => setCountdown(v)} style={{
                flex: 1, padding: "8px 0", borderRadius: 8, border: "none", cursor: "pointer",
                fontWeight: 700, fontSize: 13, fontFamily: "Montserrat, sans-serif",
                background: countdown === v ? "#038edc" : chipBg,
                color: countdown === v ? "#fff" : chipColor,
                transition: "background 0.15s ease",
              }}>
                {v}s
              </button>
            ))}
          </div>
        </div>

        <ToggleRow id="mirrorHorizontalSwitch" inverted={inverted}
          label={t("Backend.Post.Teleprompter.MirrorHorizontal")}
          checked={mirrorHorizontal} onChange={(e) => setMirrorHorizontal(e.target.checked)} />
        <ToggleRow id="mirrorVerticalSwitch" inverted={inverted}
          label={t("Backend.Post.Teleprompter.MirrorVertical")}
          checked={mirrorVertical} onChange={(e) => setMirrorVertical(e.target.checked)} />
        <ToggleRow id="invertColorsSwitch" inverted={inverted}
          label={t("Backend.Post.Teleprompter.InvertColors")}
          checked={invertColors} onChange={(e) => setInvertColors(e.target.checked)} />
      </div>

      {/* ── Layout column ── */}
      <div>
        <SectionLabel inverted={inverted}>{t("Backend.Post.Teleprompter.Headers.Layout")}</SectionLabel>

        <SettingRow label={t("Backend.Post.Teleprompter.ChangeMarginTop")} value="" unit="" inverted={inverted}>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {[0, 10, 50, 100, 200].map((v) => (
              <button key={v} onClick={() => setMarginTop(v)} style={{
                padding: "6px 14px", borderRadius: 8, border: "none", cursor: "pointer",
                fontWeight: 600, fontSize: 12, fontFamily: "Montserrat, sans-serif",
                background: chipBg, color: chipColor, transition: "background 0.15s ease",
              }}>{v}</button>
            ))}
          </div>
        </SettingRow>

        <SettingRow label={t("Backend.Post.Teleprompter.ChangeMarginBottom")} value="" unit="" inverted={inverted}>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {[10, 50, 75, 100, 200].map((v) => (
              <button key={v} onClick={() => setMarginBottom(v)} style={{
                padding: "6px 14px", borderRadius: 8, border: "none", cursor: "pointer",
                fontWeight: 600, fontSize: 12, fontFamily: "Montserrat, sans-serif",
                background: chipBg, color: chipColor, transition: "background 0.15s ease",
              }}>{v}</button>
            ))}
          </div>
        </SettingRow>

        <SettingRow label={t("Backend.Post.Teleprompter.PaddingLeft")} value={paddingLeft} unit="px" inverted={inverted}>
          <input type="range" style={sliderStyle} min={0} max={200} step={1} value={paddingLeft}
            onChange={(e) => setPaddingLeft(Number(e.target.value))} />
        </SettingRow>

        <SettingRow label={t("Backend.Post.Teleprompter.PaddingRight")} value={paddingRight} unit="px" inverted={inverted}>
          <input type="range" style={sliderStyle} min={0} max={200} step={1} value={paddingRight}
            onChange={(e) => setPaddingRight(Number(e.target.value))} />
        </SettingRow>
      </div>
    </div>
    </div>
  );
};

export default TeleprompterSettings;
